import { CircleInfoIcon } from "@xola/icons";
import {
    FormGroup,
    getSymbol,
    InlineValuePopover,
    Input,
    Label,
    Select,
    SubmitButton,
    Switch,
    Tooltip,
} from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, requiredNumberMoreThanZero, requiredString } from "common";
import React, { useState } from "react";
import { useQueryClient } from "react-query";
import {
    BxgyCoupon,
    Coupon,
    CouponTypeCopy,
    LineItemTemplate,
    LineItemType,
    Permission,
    PurchaseRuleActionObject,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
    SelectionState,
    Tag,
} from "types";
import { boolean, number, object } from "yup";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { isDefaultDemographic } from "../../models/DemographicUnit";
import { RadioInput } from "../Common/RadioInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ListFilter, SelectionWithName } from "../Dashboard/Filters/ListFilter";
import { TagInputField } from "../Field/TagInputField";
import { ContentLayout } from "../Layout/ContentLayout";
import { CouponEditorProps } from "./CouponEditorProps";
import LimitFormField from "./LimitFormField";
import { useEquipmentResourcesWithTemplates } from "@/hooks/resources/useEquipmentResourcesWithTemplates";
import { useHasEquipment } from "@/hooks/useHasEquipment";
import { useLocalizationStore } from "@/stores/localizationStore";

const validationSchema = object().shape({
    name: requiredString(),
    amount: requiredNumberMoreThanZero().when("type", {
        is: PurchaseRuleShortcutType.BXGY_PERCENT,
        then: number().max(100, "cannot be greater than 100%"),
    }),
    qualifyingQuantity: requiredNumberMoreThanZero(),
    eligibleQuantity: requiredNumberMoreThanZero().test(
        "checkQualifyingQuantity",
        "must be less than the qualifying quantity above",
        (value, form) => value! <= form.parent.qualifyingQuantity,
    ),
    excludeFee: boolean(),
});

type PurchasableItemType = "demographics" | "equipments";

interface FormValues {
    name: string;
    tags: Tag[];
    amount: number;
    qualifyingQuantity: number;
    eligibleQuantity: number;
    excludeTaxFee: boolean;
    demographics: SelectionState;
    equipment: SelectionState;
    purchasableItemType: PurchasableItemType;
    type: PurchaseRuleShortcutType;
    limit: number | null;
}

export const BxgyCouponEditor = ({ coupon, isSaving, onSubmit, onCancel }: CouponEditorProps) => {
    const seller = useSeller();
    const locale = useLocalizationStore().getLocale();
    const [isAmountOpen, setIsAmountOpen] = useState(false);
    const [isQualifyingQuantityOpen, setIsQualifyingQuantityOpen] = useState(false);
    const [isEligibleQuantityOpen, setIsEligibleQuantityOpen] = useState(false);
    const currency = getSymbol(seller.currency, locale);
    const { isGranted } = useAuthorization();
    const equipmentResources = useEquipmentResourcesWithTemplates();
    const equipmentData = (equipmentResources ?? []).map((r) => ({ id: r.templateId, name: r.name }));
    const hasEquipment = useHasEquipment();

    const handleSubmit = async (values: FormValues) => {
        onSubmit({ coupon: getCoupon(coupon as BxgyCoupon, values) });
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={getFormValues(coupon as BxgyCoupon, equipmentData)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, errors, values, setFieldValue }) => {
                const amountText =
                    values.type === PurchaseRuleShortcutType.BXGY_ABSOLUTE
                        ? `${currency}${values.amount ?? "N/A"}`
                        : `${values.amount}%`;

                return (
                    <ContentLayout isWide isDisabled={!isGranted(Permission.MODIFY_COUPONS) || isSaving}>
                        <ContentLayout.Section
                            title="Buy X Get Y"
                            description="Buy X Get Y coupons incentivize group bookings for customers while also offering a discount."
                        >
                            <FormGroup>
                                <Label>Type</Label>
                                <Input disabled value={CouponTypeCopy.bxgy} />
                            </FormGroup>

                            <FormField name="name" label="Campaign Name">
                                <InputField isRequired placeholder="Campaign name" />
                            </FormField>

                            {coupon?.limit && <LimitFormField />}

                            <FormField name="tags" label="Booking Tags">
                                <TagInputField />
                            </FormField>

                            <FormGroup>
                                <Label>
                                    Purchasable Item Type
                                    <Tooltip
                                        content="Purchasable Item type refers to demographics or equipment explicitly purchased by the customer. Excludes automatically assigned equipment and optional demographics collected during equipment experiences."
                                        className="ml-1"
                                        placement="right"
                                    >
                                        <CircleInfoIcon />
                                    </Tooltip>
                                </Label>
                                <div className="flex space-x-4">
                                    <RadioInput
                                        checked={values.purchasableItemType === "demographics"}
                                        onChange={() => setFieldValue("purchasableItemType", "demographics")}
                                    >
                                        Demographics
                                    </RadioInput>
                                    {hasEquipment && (
                                        <RadioInput
                                            checked={values.purchasableItemType === "equipments"}
                                            onChange={() => setFieldValue("purchasableItemType", "equipments")}
                                        >
                                            Equipments
                                        </RadioInput>
                                    )}
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label>Terms</Label>

                                <div className="flex space-x-1">
                                    <span>For every</span>

                                    <span data-testid="bxgy-coupon-editor-demographics-amount">
                                        <InlineValuePopover
                                            text={String(values.qualifyingQuantity)}
                                            error={errors?.qualifyingQuantity}
                                            placement="top-start"
                                            isOpen={isQualifyingQuantityOpen}
                                            classNames={{
                                                text: clsx(
                                                    "px-0.5 pb-0.5",
                                                    errors?.qualifyingQuantity
                                                        ? "text-danger border-danger"
                                                        : "border-black",
                                                ),
                                            }}
                                            onClick={() => setIsQualifyingQuantityOpen(!isQualifyingQuantityOpen)}
                                        >
                                            <div>
                                                <Label>
                                                    {values.purchasableItemType === "equipments"
                                                        ? "Equipments"
                                                        : "Demographics"}
                                                </Label>

                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={String(values.qualifyingQuantity)}
                                                    onKeyDown={handleKeyDown}
                                                    onBlur={() => {
                                                        setIsQualifyingQuantityOpen(!isQualifyingQuantityOpen);
                                                    }}
                                                    onChange={(e: any) => {
                                                        setFieldValue(
                                                            "qualifyingQuantity",
                                                            Number(e.target.value || 0),
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </InlineValuePopover>
                                    </span>

                                    {values.purchasableItemType === "equipments" ? (
                                        <ListFilter
                                            key="equipments"
                                            label="Equipments"
                                            title="Filter Equipments"
                                            fetch={() => ({
                                                data: equipmentData,
                                                isLoading: false,
                                                hasNextPage: false,
                                                fetchNextPage: () => undefined,
                                                isFetchingNextPage: false,
                                            })}
                                            defaultParams={{ limit: 1000 }}
                                            value={values.equipment ?? { all: true, selected: [] }}
                                            target={EquipmentSummary}
                                            show={{ search: false }}
                                            onChange={(equipment) => {
                                                setFieldValue("equipment", equipment);
                                            }}
                                        />
                                    ) : (
                                        <ListFilter
                                            key="demographics"
                                            label="Demographics"
                                            title="Filter Demographics"
                                            collection={["lineItemTemplates"]}
                                            value={values.demographics}
                                            defaultParams={{
                                                type: "demographic",
                                                limit: 1000,
                                                "code[notEqual]": "per_outing_price",
                                            }}
                                            target={DemographicSummary}
                                            onChange={(demographics) => {
                                                setFieldValue("demographics", demographics);
                                            }}
                                        />
                                    )}

                                    <span>booked</span>
                                </div>

                                <div className="mt-2 flex space-x-1">
                                    <span>Get</span>

                                    <span data-testid="bxgy-coupon-editor-booked-amount">
                                        <InlineValuePopover
                                            text={String(values.eligibleQuantity)}
                                            error={errors?.eligibleQuantity}
                                            placement="top-start"
                                            isOpen={isEligibleQuantityOpen}
                                            classNames={{
                                                text: clsx(
                                                    "px-0.5 pb-0.5",
                                                    errors?.eligibleQuantity
                                                        ? "text-danger border-danger"
                                                        : "border-black",
                                                ),
                                            }}
                                            onClick={() => {
                                                setIsEligibleQuantityOpen(!isEligibleQuantityOpen);
                                            }}
                                        >
                                            <div>
                                                <Label>
                                                    {values.purchasableItemType === "equipments"
                                                        ? "Equipments"
                                                        : "Demographics"}
                                                </Label>

                                                <Input
                                                    type="number"
                                                    value={String(values.eligibleQuantity)}
                                                    onKeyDown={handleKeyDown}
                                                    onBlur={() => {
                                                        setIsEligibleQuantityOpen(!isEligibleQuantityOpen);
                                                    }}
                                                    onChange={(e: any) => {
                                                        setFieldValue("eligibleQuantity", Number(e.target.value || 0));
                                                    }}
                                                />
                                            </div>
                                        </InlineValuePopover>
                                    </span>

                                    <span data-testid="bxgy-coupon-editor-value">
                                        {values.purchasableItemType === "equipments"
                                            ? "equipment(s) of equal or lesser value for"
                                            : "demographic(s) of equal or lesser value for"}
                                    </span>

                                    <InlineValuePopover
                                        data-testid="bxgy-coupon-editor-value"
                                        text={amountText}
                                        error={errors?.amount}
                                        placement="top-start"
                                        // So that when amount increases, the popover doesn't move
                                        isOpen={isAmountOpen}
                                        classNames={{
                                            text: clsx(
                                                "no-translate",
                                                errors?.amount ? "text-danger border-danger" : "border-black",
                                            ),
                                        }}
                                        onClick={() => {
                                            setIsAmountOpen(!isAmountOpen);
                                        }}
                                        onClickOutside={() => {
                                            setIsAmountOpen(false);
                                        }}
                                    >
                                        <div>
                                            <Label>Amount</Label>
                                            <div className="flex space-x-2">
                                                <Input
                                                    isRequired
                                                    type="number"
                                                    value={String(values.amount)}
                                                    onKeyDown={handleKeyDown}
                                                    onChange={(e: any) => {
                                                        setFieldValue("amount", Number(e.target.value));
                                                    }}
                                                />
                                                <CouponEditorType
                                                    values={values}
                                                    currency={currency}
                                                    onChange={(e: any) => {
                                                        setFieldValue("type", e.target.value);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </InlineValuePopover>
                                    <span>off</span>
                                </div>
                            </FormGroup>

                            <div>
                                <Switch.Group>
                                    <Switch
                                        isChecked={values.excludeTaxFee}
                                        onChange={(value: boolean) => {
                                            setFieldValue("excludeTaxFee", value);
                                        }}
                                    />

                                    <Switch.Label direction="right" className="text-gray-darker">
                                        Remove taxes and fees
                                    </Switch.Label>
                                </Switch.Group>

                                <div className="pl-10 text-sm text-gray-dark">
                                    If enabled, all taxes and fees will be removed from a booking when a code from this
                                    campaign is applied
                                </div>
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Footer>
                            <SubmitButton type="submit" isLoading={isSaving} onClick={handleSubmit}>
                                {coupon.id ? "Save" : "Next"}
                            </SubmitButton>

                            <SecondaryButton
                                type="reset"
                                onClick={() => {
                                    onCancel();
                                }}
                            >
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
        // Trigger blur on pressing enter so the form is closed
        e.target.blur();
    }
};

interface CouponEditorTypeProps {
    values: FormValues;
    currency: string;
    onChange: (e: any) => void;
}

const CouponEditorType = ({ values, currency, onChange }: CouponEditorTypeProps) => {
    return (
        <Select
            data-testid="bxgy-coupon-editor-amount-type"
            className="!w-15 cursor-pointer"
            value={values.type}
            onChange={onChange}
        >
            <option value={PurchaseRuleShortcutType.BXGY_ABSOLUTE} className="no-translate">
                {currency}
            </option>
            <option value={PurchaseRuleShortcutType.BXGY_PERCENT}>%</option>
        </Select>
    );
};

const DemographicSummary = ({ value, onClick }: { value: SelectionWithName; onClick: () => void }) => {
    const queryClient = useQueryClient();

    const demographicList = value.selected.map((item) => {
        const demographic = queryClient.getQueryData<LineItemTemplate>(["lineItemTemplates", item.id]);
        return demographic?.name ?? item.name ?? "";
    });

    return (
        <Tooltip content={demographicList.length > 0 ? getDemographicText(demographicList) : "demographics"}>
            <span
                className="inline-block max-w-[250px] truncate border-b font-bold hover:cursor-pointer"
                onClick={onClick}
            >
                {demographicList.length > 0 ? getDemographicText(demographicList) : "demographics"}
            </span>
        </Tooltip>
    );
};

const EquipmentSummary = ({ onClick }: { onClick: () => void }) => {
    return (
        <span className="inline-block max-w-[250px] truncate border-b font-bold hover:cursor-pointer" onClick={onClick}>
            Equipments
        </span>
    );
};

const getDemographicText = (demographics: string[]) => {
    const { length } = demographics;
    const elements = demographics.map((name, index) => {
        const element = <span className={clsx(!isDefaultDemographic(name) && "no-translate")}>{name}</span>;
        if (index < length - 1) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                    {element}
                    <span>, </span>
                </React.Fragment>
            );
        }

        return element;
    });

    return elements;
};

const getCoupon = (coupon: BxgyCoupon, values: FormValues): Coupon => {
    const [action] = coupon.purchaseRule?.actions ?? [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { type: shortcutType, demographics, equipment, purchasableItemType, ...couponValues } = values;

    const isEquipment = values.purchasableItemType === "equipments";
    const selectedIds = isEquipment ? values.equipment.selected : values.demographics.selected;

    return {
        ...coupon,
        ...couponValues,
        limit: Number(values?.limit) === 0 ? null : values?.limit,
        purchaseRule: {
            name: coupon.purchaseRule?.name ?? "",
            seller: { id: coupon.seller.id },
            actions: [
                {
                    id: action?.id,
                    object: PurchaseRuleActionObject.UPDATE_LINE_ITEM_ACTION,
                    shortcut: {
                        type: shortcutType,
                        amount: values.amount,
                        options: {
                            templateIds: selectedIds,
                            templateCodes: isEquipment
                                ? values.equipment.all
                                    ? [PurchaseRuleShortcutTemplateCode.EQUIPMENT]
                                    : undefined
                                : values.demographics.all
                                ? [PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC]
                                : undefined,
                            templateTypes: isEquipment ? [LineItemType.EQUIPMENT] : undefined,
                            qualifyingQuantity: values.qualifyingQuantity,
                            eligibleQuantity: values.eligibleQuantity,
                        },
                    },
                },
            ],
        },
    };
};

const getFormValues = (coupon: BxgyCoupon, equipmentData: { id: string; name: string }[]): FormValues => {
    const [action] = coupon.purchaseRule?.actions ?? [];

    if (!action) {
        return initialValues;
    }

    /**
     * Coupons, Taxes uses shortcut, so shortcut will be there always
     */
    const opts = (action.shortcut!.options ?? {}) as any;
    const { templateIds = [], templateCodes = [], templateTypes = [], qualifyingLineItemTemplates = [] } = opts;
    const resolvedIds =
        qualifyingLineItemTemplates.length > 0 ? qualifyingLineItemTemplates.map((t: any) => t.id ?? t) : templateIds;
    const hasEquipmentIds = resolvedIds.some((id: string) => equipmentData?.some((e) => e.id === id));
    const isEquipment =
        (templateCodes as string[]).includes(PurchaseRuleShortcutTemplateCode.EQUIPMENT) ||
        (templateTypes as string[]).includes(LineItemType.EQUIPMENT) ||
        hasEquipmentIds;

    return {
        ...initialValues,
        name: coupon.name,
        tags: coupon.tags,
        limit: Number(coupon?.limit) === 0 ? null : coupon?.limit,
        amount: action.shortcut!.amount,
        excludeTaxFee: coupon.excludeTaxFee ?? initialValues.excludeTaxFee,
        purchasableItemType: isEquipment ? "equipments" : "demographics",
        demographics: { all: !isEquipment && resolvedIds.length === 0, selected: isEquipment ? [] : resolvedIds },
        equipment: { all: isEquipment && resolvedIds.length === 0, selected: isEquipment ? resolvedIds : [] },
        type: action.shortcut!.type,
        qualifyingQuantity: opts.qualifyingQuantity ?? initialValues.qualifyingQuantity,
        eligibleQuantity: opts.eligibleQuantity ?? initialValues.eligibleQuantity,
    };
};

const initialValues: FormValues = {
    name: "",
    tags: [],
    amount: 0,
    qualifyingQuantity: 1,
    eligibleQuantity: 1,
    excludeTaxFee: false,
    demographics: { all: true, selected: [] },
    equipment: { all: true, selected: [] },
    purchasableItemType: "demographics",
    type: PurchaseRuleShortcutType.BXGY_ABSOLUTE,
    limit: null,
};
